import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useUserAuth } from "../lib/userAuth";

const links = [
  { href: "#home", label: "Home" },
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#doors", label: "Doors" },
  { href: "#projects", label: "Projects" },
  { href: "#materials", label: "Materials" },
  { href: "#contact", label: "Contact" },
];

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.3-1.6 3.8-5.4 3.8-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.3 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c6.9 0 9.5-4.8 9.5-8.2 0-.6-.1-1-.1-1.2H12z"/>
    </svg>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, login, logout } = useUserAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F9F8F4]/95 backdrop-blur-md border-b border-[#E5E0D5]"
          : "bg-[#F9F8F4]/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <div className="leading-tight">
            <div className="font-serif text-xl text-[#2B2623] tracking-tight">
              Bolt <span className="italic text-[#8C5931]">Modular</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6E6862]">A unit of JJ Lumbers</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm text-[#2B2623] hover:text-[#8C5931] transition-colors relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-px after:bg-[#8C5931] hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <div className="hidden md:flex items-center gap-2" data-testid="user-chip">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-[#E5E0D5]"/>
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#8C5931] text-white grid place-items-center text-xs">
                  {(user.name || user.email || "?").slice(0,1).toUpperCase()}
                </div>
              )}
              <div className="leading-tight">
                <div className="text-xs text-[#2B2623] font-medium max-w-[130px] truncate">{user.name || user.email}</div>
                <button data-testid="user-logout" onClick={logout} className="text-[10px] uppercase tracking-[0.2em] text-[#8C5931] hover:text-[#2B2623] flex items-center gap-1">
                  <LogOut size={10}/> Sign out
                </button>
              </div>
            </div>
          ) : (
            <button
              data-testid="google-signin-btn"
              onClick={login}
              className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E5E0D5] rounded-full text-sm text-[#2B2623] hover:border-[#8C5931] transition-colors"
            >
              <GoogleIcon/> Sign in
            </button>
          )}
          <a
            href="#contact"
            data-testid="navbar-cta"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-[#2B2623] text-[#F9F8F4] text-sm hover:bg-[#8C5931] transition-colors duration-300 rounded-full"
          >
            Request a Quote
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F9F8F4] border-t border-[#E5E0D5]">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-base text-[#2B2623] hover:text-[#8C5931]">
                {l.label}
              </a>
            ))}
            {user ? (
              <button onClick={logout} className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#E5E0D5] rounded-full text-sm">
                <LogOut size={14}/> Sign out — {user.name || user.email}
              </button>
            ) : (
              <button onClick={login} className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#E5E0D5] rounded-full text-sm">
                <GoogleIcon/> Sign in with Google
              </button>
            )}
            <a href="#contact" onClick={() => setOpen(false)} className="inline-flex items-center justify-center px-5 py-3 bg-[#2B2623] text-[#F9F8F4] rounded-full text-sm">
              Request a Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
